import { useState } from 'react';
import { getMsalInstance } from '../auth';
import { addCriterionV2 } from '../api/v2/criteria';
import type { CriterionAPIV2, CriterionTier } from '../types';

interface Props {
  tenderId: string;
  /**
   * Called with the newly persisted criterion after a successful POST.
   * Parent appends to its local tender.criteria state — no refetch.
   */
  onCriterionAdded: (added: CriterionAPIV2) => void;
}

type AddKind = 'REQUIREMENT' | 'RESPONSIBILITY';

const TIER_LABELS: Record<CriterionTier, string> = {
  MUST_HAVE: 'Must have',
  STATED: 'Stated',
  NICE_TO_HAVE: 'Nice-to-have',
};

/**
 * Right-rail form tile for adding a criterion surfaced from a recruiter
 * conversation. Mirrors the wireframe shape — text + kind + tier + optional
 * note + reanalyze toggle.
 *
 * <p>The POST is synchronous: the backend runs a slim LLM call inline,
 * persists, and returns the full criterion. The parent appends to its
 * local list — no polling, no refetch.</p>
 */
export default function AddCriterionTile({ tenderId, onCriterionAdded }: Props) {
  const [text, setText] = useState('');
  const [kind, setKind] = useState<AddKind>('REQUIREMENT');
  const [tier, setTier] = useState<CriterionTier>('MUST_HAVE');
  const [note, setNote] = useState('');
  const [reanalyze, setReanalyze] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedText = text.trim();
  const canSubmit = trimmedText.length > 0 && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const added = await addCriterionV2(tenderId, {
        text: trimmedText,
        kind,
        tier,
        note: note.trim() || undefined,
        user_id: getMsalInstance().getActiveAccount()?.username ?? undefined,
        reanalyze,
      });
      onCriterionAdded(added);
      // Reset for next add; leave kind/tier/reanalyze on the recruiter's
      // last choice since those are likely to repeat across a session.
      setText('');
      setNote('');
    } catch {
      setError("Couldn't save — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <aside className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">Add criterion</h2>
        <p className="text-[11px] text-gray-500 mt-1 leading-snug">
          Add a requirement or responsibility surfaced from a conversation with the intermediary.
        </p>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <label
            htmlFor="add-criterion-text"
            className="text-[10px] font-semibold uppercase tracking-wider text-gray-400"
          >
            Criterion
          </label>
          <textarea
            id="add-criterion-text"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. SAP HANA migration experience"
            disabled={submitting}
            maxLength={1000}
            className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              htmlFor="add-criterion-kind"
              className="text-[10px] font-semibold uppercase tracking-wider text-gray-400"
            >
              Kind
            </label>
            <select
              id="add-criterion-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as AddKind)}
              disabled={submitting}
              className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="REQUIREMENT">Requirement</option>
              <option value="RESPONSIBILITY">Responsibility</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="add-criterion-tier"
              className="text-[10px] font-semibold uppercase tracking-wider text-gray-400"
            >
              Tier
            </label>
            <select
              id="add-criterion-tier"
              value={tier}
              onChange={(e) => setTier(e.target.value as CriterionTier)}
              disabled={submitting}
              className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="MUST_HAVE">{TIER_LABELS.MUST_HAVE}</option>
              <option value="STATED">{TIER_LABELS.STATED}</option>
              <option value="NICE_TO_HAVE">{TIER_LABELS.NICE_TO_HAVE}</option>
            </select>
          </div>
        </div>

        <div>
          <label
            htmlFor="add-criterion-note"
            className="text-[10px] font-semibold uppercase tracking-wider text-gray-400"
          >
            Note (optional)
          </label>
          <input
            id="add-criterion-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. per Hays call"
            disabled={submitting}
            maxLength={500}
            className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <label
          htmlFor="add-criterion-reanalyze"
          className="flex items-start gap-2.5 cursor-pointer pt-1"
        >
          <input
            id="add-criterion-reanalyze"
            type="checkbox"
            checked={reanalyze}
            onChange={(e) => setReanalyze(e.target.checked)}
            disabled={submitting}
            className="mt-0.5 accent-brand-600"
            aria-label="Re-analyze previously analyzed candidates"
          />
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-900 leading-snug">
              Re-analyze previously analyzed candidates
            </p>
            <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
              Accepted; not actioned in MVP (no deep-analysis pipeline yet).
            </p>
          </div>
        </label>

        {error && (
          <p className="text-[11px] text-red-600">{error}</p>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5"
        >
          {submitting ? 'Saving…' : '+ Add criterion'}
        </button>
      </div>
    </aside>
  );
}
